import { PerspectiveCamera } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import {
  type ReactNode,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Bone,
  LineBasicMaterial,
  type Object3D,
  SkeletonHelper,
} from 'three';

import type { CharacterMeshFormat } from '@cartwhl/3d/player';
import {
  Player,
  usePlayerActions,
  usePlayerTimeline,
} from '@cartwhl/3d/player';
import { getResourcesUrl } from '@cartwhl/common/utils';
import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Input from '@cartwhl/ui/atoms/Input';
import Loading from '@cartwhl/ui/atoms/Loading';
import Select from '@cartwhl/ui/atoms/Select';
import Slider from '@cartwhl/ui/atoms/Slider';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import DownloadIcon from '@cartwhl/ui/icons/DownloadIcon';
import LinkIcon from '@cartwhl/ui/icons/LinkIcon';
import PauseIcon from '@cartwhl/ui/icons/PauseIcon';
import PlayIcon from '@cartwhl/ui/icons/PlayIcon';
import Button from '@cartwhl/ui/input/Button';
import GoButton from '@cartwhl/ui/input/GoButton';
import Modal from '@cartwhl/ui/molecules/Modal';

import { noticesStore } from '../../../../../stores/global';
import { RetargetRefreshIcon } from '../../../presentations/icons';
import CartographerMotionPreviewAssetPlayer from './CartographerMotionPreviewAssetPlayer';
import CartographerPreviewCharacter from '../CartographerPreviewCharacter';
import { CHARACTER_ROTATION } from '../../../../MotionEditor/constants';
import {
  DEFAULT_MOTION_EXPORT_SETTINGS,
  downloadCharacterMotionExport,
  getCharacterMeshDownloadProfile,
  MOTION_DOWNLOAD_FPS_OPTIONS,
  type MotionConfigSource,
  type MotionExportSettings,
  resolveMotionNameSlug,
  sanitizeFileNameSegment,
} from '../motionExport';
import {
  resolveMotionPreviewAssetKind,
  resolveMotionPreviewDownloadType,
  type CartographerMotionPreviewAssetKind,
} from '../motionPreviewAsset';
import {
  getMotionExportSettingsKey,
  type CartographerMotionRetargetStatus,
} from '../motionRetargetStatus';
import useCartographerMotionRetargetPreview, {
  AXEL_CHARACTER_ID,
} from '../useCartographerMotionRetargetPreview';
import useMotionPreviewGltfAsset from '../useMotionPreviewGltfAsset';

export type CartographerMotionPreviewModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apiKey: string;
  characterID: string;
  meshPath: string;
  meshFile?: File | null;
  /** Required when meshPath is a blob URL (e.g. from a loaded file). */
  meshFormat?: CharacterMeshFormat;
  /** Canvas motion job id (e.g. from the asset library). Loads this motion for Axel and your character via getMotion. */
  externalMotionId?: string | null;
  /** Short label when using externalMotionId (optional). */
  externalMotionLabel?: string | null;
  /** Zero-based body index for multi-person comic jobs. */
  bodyIndex?: number;
  /**
   * Human-readable character name for debug BVH filenames (e.g. header name without extension).
   */
  userCharacterDisplayName?: string | null;
  /** Whether preview should communicate saved vs unsaved mapping state. */
  characterMappingState?: 'saved' | 'unsaved';
  /** Used to determine whether a cached preview is stale. */
  mappingRevisionKey?: string | null;
  configSource?: MotionConfigSource;
  prepareForUserMotionRequest?: (options: {
    configSource: MotionConfigSource;
    mode: 'auto' | 'manual';
  }) => Promise<boolean> | boolean;
  motionExportSettings?: MotionExportSettings;
  onMotionExportSettingsChange?: (settings: MotionExportSettings) => void;
  onRetargetStatusChange?: (status: CartographerMotionRetargetStatus) => void;
  onSwitchToUnsavedRetarget?: () => Promise<boolean> | boolean;
  /**
   * When set with `externalMotionId`, shows a header control to leave the preview and return to the motion picker (search state is owned by the parent).
   */
  onBackToMotionSearch?: () => void;
};

const PREVIEW_ACTOR_AXEL = 'cartographer-preview-axel';
const PREVIEW_ACTOR_USER = 'cartographer-preview-user';

const PREVIEW_CANVAS_CLASS =
  'h-[min(320px,42svh)] w-full min-h-[220px] rounded-xl sm:h-[min(360px,45svh)] sm:min-h-[240px] lg:h-[min(420px,50svh)] lg:min-h-[260px]';
const COMPACT_PREVIEW_CANVAS_CLASS =
  'h-[min(240px,30svh)] w-full min-h-[180px] rounded-xl sm:h-[min(260px,32svh)] sm:min-h-[200px] lg:h-[min(280px,34svh)] lg:min-h-[220px]';
const DENSE_PREVIEW_CANVAS_CLASS =
  'h-[min(190px,24svh)] w-full min-h-[140px] rounded-xl sm:h-[min(220px,26svh)] sm:min-h-[160px] lg:h-[min(240px,28svh)] lg:min-h-[180px]';

/** Public Axel reference mesh (Cartographer motion preview). */
const AXEL_REFERENCE_MESH_URL = `${getResourcesUrl(false).replace(/\/share$/, '')}/character-uploads/FREE_CHARACTERS/char-upload-Axel/character.glb`;

/** Best-effort download for presigned BVH URLs (debug). */
async function downloadBvhFromUrl(url: string, filename: string) {
  const baseName = filename.toLowerCase().endsWith('.bvh')
    ? filename.slice(0, -4)
    : filename;
  const downloadName = `${baseName}.bvh`;
  try {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = downloadName;
    a.rel = 'noopener';
    a.click();
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

function PreviewBvhDownloadButton({
  bvhUrl,
  filename,
  label,
}: {
  bvhUrl: string;
  filename: string;
  label: string;
}) {
  return (
    <button
      type="button"
      title={`Download BVH (${label}, debug)`}
      aria-label={`Download BVH for ${label}`}
      className="bg-primary/85 border-primary text-primary hover:bg-primary dark:bg-gray-85/90 dark:hover:bg-gray-75 absolute right-2 top-2 z-10 flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border shadow-md backdrop-blur-sm transition-colors"
      onClick={() => downloadBvhFromUrl(bvhUrl, filename)}
    >
      <DownloadIcon className="h-4 w-4" />
    </button>
  );
}

/** Slider shows 1-based frames like AnimationPlayer MiniControls; transport is 0-based. */
function previewSliderDisplayFrame(zeroBased: number) {
  return zeroBased + 1;
}
function previewSliderToZeroBased(display: number) {
  return display - 1;
}

function SyncedDualPlaybackControls({
  actorIdAxel,
  actorIdUser,
  axelReady,
}: {
  actorIdAxel: string;
  actorIdUser: string;
  axelReady: boolean;
}) {
  const { isPlaying, currentFrame, duration } = usePlayerTimeline();
  const axelActions = usePlayerActions(actorIdAxel);
  const userActions = usePlayerActions(actorIdUser);

  const frames = duration.frames;
  const timelineDisabled = frames <= 0;

  const setSyncedFrame = (frame: number) => {
    const maxIndex = Math.max(0, frames - 1);
    const clamped = Math.max(0, Math.min(Math.round(frame), maxIndex));
    if (axelReady) axelActions.setFrame(clamped);
    userActions.setFrame(clamped);
  };

  const toggle = () => {
    if (isPlaying) {
      if (axelReady) axelActions.pause();
      userActions.pause();
      return;
    }
    if (axelReady) axelActions.play();
    userActions.play();
  };

  const pauseIfPlayingBeforeScrub = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (timelineDisabled || !isPlaying) return;
      if (event.button !== 0) return;
      if (axelReady) axelActions.pause();
      userActions.pause();
    },
    [axelActions, axelReady, isPlaying, timelineDisabled, userActions],
  );

  return (
    <div className="pointer-events-auto flex w-full min-w-0 flex-wrap items-center gap-x-2 gap-y-1.5">
      <GoButton className="shrink-0" onClick={toggle}>
        {isPlaying ? (
          <PauseIcon className="pointer-events-none" />
        ) : (
          <PlayIcon className="pointer-events-none" />
        )}
      </GoButton>
      <div
        className="flex h-10 min-w-0 flex-1 basis-[220px] items-center px-2"
        onPointerDownCapture={pauseIfPlayingBeforeScrub}
      >
        <Slider
          currentValue={previewSliderDisplayFrame(currentFrame)}
          min={previewSliderDisplayFrame(0)}
          max={previewSliderDisplayFrame(Math.max(0, frames - 1))}
          size="sm"
          step={1}
          onChange={(value) => {
            setSyncedFrame(previewSliderToZeroBased(value));
          }}
          trackColor="player"
          rangeColor="player"
          disabled={timelineDisabled}
        />
      </div>
      {!!frames && (
        <Text
          as="div"
          variant="studio/body/medium/sm"
          className="text-gray-90 dark:text-gray-40 ml-auto shrink-0 text-end tabular-nums opacity-60"
          style={{
            minWidth: `${Math.max(duration.frames.toString().length, 3) * 16}px`,
          }}
        >
          {currentFrame + 1}/{duration.frames}
        </Text>
      )}
    </div>
  );
}

function MotionPreviewSection({
  title,
  open,
  onToggle,
  children,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className="border-primary border-b px-4 py-2.5 sm:px-6">
      <button
        type="button"
        className="hover:bg-gray-15/45 dark:hover:bg-gray-75/40 flex w-full min-w-0 items-center gap-2 rounded-lg px-1.5 py-2 text-left transition-colors"
        onClick={onToggle}
        aria-expanded={open}
        aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
      >
        <span className="text-gray-60 flex h-6 w-6 shrink-0 items-center justify-center">
          <ChevronRightIcon
            className={`h-5 w-5 transition-transform ${open ? 'rotate-90' : ''}`}
          />
        </span>
        <Text variant="studio/body/medium/sm" className="text-primary">
          {title}
        </Text>
      </button>
      {open && <div className="pt-2.5">{children}</div>}
    </div>
  );
}

/**
 * R3F component that renders a Three.js SkeletonHelper for any Object3D
 * that contains Bone descendants. Works with arbitrary FBX/GLB files
 * regardless of whether the model uses SkinnedMesh or plain bone hierarchies
 * (unlike applyShowSkeleton which depends on _geo/_skl naming conventions).
 *
 * The helper is added to the R3F root scene — NOT as a child of the character
 * scene. SkeletonHelper shares `this.matrix` with `root.matrixWorld` and sets
 * `matrixAutoUpdate = false`, so adding it as a child of the root object
 * would double-apply the parent transform in `updateMatrixWorld`.
 */
function PreviewSkeletonHelper({ scene }: { scene: Object3D | null }) {
  const r3fScene = useThree((s) => s.scene);

  useEffect(() => {
    if (!scene) return;

    let hasBones = false;
    scene.traverse((obj) => {
      if (obj instanceof Bone) hasBones = true;
    });
    if (!hasBones) return;

    const helper = new SkeletonHelper(scene);
    const mat = helper.material as LineBasicMaterial;
    mat.vertexColors = false;
    mat.color.set(0x0fd23a);
    r3fScene.add(helper);

    return () => {
      r3fScene.remove(helper);
      helper.dispose();
    };
  }, [scene, r3fScene]);

  return null;
}

function DualMotionPreviewPlayers({
  axelBvhUrl,
  axelLoading,
  userMotionUrl,
  userAssetKind,
  characterID,
  userCharacterPanelTitle,
  meshPath,
  meshFile,
  meshFormat,
  exportFps,
  exportFrameStep,
  exportRootMotion,
  referenceBvhDownloadFilename,
  userBvhDownloadFilename,
  compactHeight,
  denseHeight,
}: {
  axelBvhUrl: string | null;
  axelLoading: boolean;
  userMotionUrl: string;
  userAssetKind: CartographerMotionPreviewAssetKind;
  characterID: string;
  userCharacterPanelTitle: string;
  meshPath: string;
  meshFile?: File | null;
  meshFormat?: CharacterMeshFormat;
  exportFps: number;
  exportFrameStep: number;
  exportRootMotion: boolean;
  referenceBvhDownloadFilename: string;
  userBvhDownloadFilename: string;
  compactHeight: boolean;
  denseHeight: boolean;
}) {
  const [showSkeletonHelper, setShowSkeletonHelper] = useState(false);
  const axelReady = Boolean(axelBvhUrl);
  const showAxelLoading = !axelReady && axelLoading;
  const previewCanvasClassName = denseHeight
    ? DENSE_PREVIEW_CANVAS_CLASS
    : compactHeight
      ? COMPACT_PREVIEW_CANVAS_CLASS
      : PREVIEW_CANVAS_CLASS;
  const {
    asset: userMotionAsset,
    error: userMotionAssetError,
    isLoading: userMotionAssetLoading,
  } = useMotionPreviewGltfAsset({
    enabled: userAssetKind === 'gltf' && Boolean(userMotionUrl),
    gltfUrl: userAssetKind === 'gltf' ? userMotionUrl : null,
  });

  const [axelCharacterScene, setAxelCharacterScene] = useState<Object3D | null>(
    null,
  );
  const [userCharacterScene, setUserCharacterScene] = useState<Object3D | null>(
    null,
  );

  const previewFitKeySuffix = useMemo(
    () => `${exportFps}|${exportFrameStep}|${exportRootMotion}`,
    [exportFps, exportFrameStep, exportRootMotion],
  );
  const axelCameraFitKey = axelBvhUrl
    ? `${axelBvhUrl}|${previewFitKeySuffix}`
    : '';
  const userCameraFitKey = `${userAssetKind}|${userMotionUrl}|${previewFitKeySuffix}`;
  const userPreviewReady =
    userAssetKind === 'gltf' ? Boolean(userMotionAsset) : Boolean(userMotionUrl);
  const userPreviewError = userMotionAssetError?.message ?? null;
  const showUserLoading =
    userAssetKind === 'gltf' && !userPreviewReady && userMotionAssetLoading;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex min-h-0 min-w-0 flex-col gap-3 lg:flex-1 lg:flex-row lg:gap-4">
        <div className="flex min-w-0 flex-col gap-1.5 lg:min-h-0 lg:flex-1">
          <Text
            variant="studio/body/medium/xs"
            className="dark:text-gray-40 shrink-0 px-0.5 text-gray-50"
          >
            Axel (reference)
          </Text>
          {axelReady ? (
            <div
              className={`relative w-full ${previewCanvasClassName} lg:min-h-0 lg:flex-1`}
            >
              <PreviewBvhDownloadButton
                bvhUrl={axelBvhUrl!}
                filename={referenceBvhDownloadFilename}
                label="Axel"
              />
              <Player.Canvas className="h-full min-h-[inherit] w-full rounded-xl">
                <Player.Environment />
                <PerspectiveCamera makeDefault position={[0, 1, 10]} zoom={2} />
                <Player.CameraControls
                  fitCharacter
                  fitCharacterKey={axelCameraFitKey}
                  fitCharacterTarget={axelCharacterScene}
                />
                <Player.Character
                  actorId={PREVIEW_ACTOR_AXEL}
                  characterID={AXEL_CHARACTER_ID}
                  meshPath={AXEL_REFERENCE_MESH_URL}
                  meshFormat="glb"
                  bvhUrl={axelBvhUrl!}
                  rotation={CHARACTER_ROTATION}
                  onSceneReady={setAxelCharacterScene}
                />
                {showSkeletonHelper && (
                  <PreviewSkeletonHelper scene={axelCharacterScene} />
                )}
              </Player.Canvas>
            </div>
          ) : (
            <div
              className={`border-primary bg-secondary/40 flex items-center justify-center rounded-xl border border-dashed px-3 py-8 text-center ${previewCanvasClassName} lg:min-h-0 lg:flex-1`}
            >
              <div className="text-secondary flex max-w-[260px] flex-col items-center gap-2">
                {showAxelLoading ? (
                  <Loading className="h-5 w-5" strokeWidth={3} />
                ) : null}
                <Text variant="studio/body/medium/sm" className="text-center">
                  {showAxelLoading
                    ? 'Loading reference preview…'
                    : 'Reference preview is not available yet.'}
                </Text>
              </div>
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-col gap-1.5 lg:min-h-0 lg:flex-1">
          <Text
            variant="studio/body/medium/xs"
            className="dark:text-gray-40 shrink-0 px-0.5 text-gray-50"
          >
            {userCharacterPanelTitle}
          </Text>
          <div
            className={`relative w-full ${previewCanvasClassName} lg:min-h-0 lg:flex-1`}
          >
            {userAssetKind === 'bvh' ? (
              <PreviewBvhDownloadButton
                bvhUrl={userMotionUrl}
                filename={userBvhDownloadFilename}
                label={userCharacterPanelTitle}
              />
            ) : null}
            {userAssetKind === 'gltf' && !userPreviewReady ? (
              <div className="border-primary bg-secondary/40 flex h-full items-center justify-center rounded-xl border border-dashed px-3 py-8 text-center">
                <div className="text-secondary flex max-w-[260px] flex-col items-center gap-2">
                  {showUserLoading ? (
                    <Loading className="h-5 w-5" strokeWidth={3} />
                  ) : null}
                  <Text
                    variant="studio/body/medium/sm"
                    className={`text-center ${userPreviewError ? 'text-red-600' : ''}`}
                  >
                    {userPreviewError
                      ? userPreviewError
                      : showUserLoading
                        ? 'Loading motion preview…'
                        : 'Motion preview is not available yet.'}
                  </Text>
                </div>
              </div>
            ) : (
              <Player.Canvas className="h-full min-h-[inherit] w-full rounded-xl">
                <Player.Environment />
                <PerspectiveCamera makeDefault position={[0, 1, 10]} zoom={2} />
                <Player.CameraControls
                  fitCharacter
                  fitCharacterKey={userCameraFitKey}
                  fitCharacterTarget={userCharacterScene}
                />
                {userAssetKind === 'gltf' && userMotionAsset ? (
                  <CartographerMotionPreviewAssetPlayer
                    actorId={PREVIEW_ACTOR_USER}
                    motionAsset={userMotionAsset}
                    onSceneReady={setUserCharacterScene}
                  />
                ) : (
                  <CartographerPreviewCharacter
                    actorId={PREVIEW_ACTOR_USER}
                    characterID={characterID}
                    meshFile={meshFile}
                    meshPath={meshPath}
                    meshFormat={meshFormat}
                    bvhUrl={userMotionUrl}
                    onSceneReady={setUserCharacterScene}
                  />
                )}
                {showSkeletonHelper && (
                  <PreviewSkeletonHelper scene={userCharacterScene} />
                )}
              </Player.Canvas>
            )}
          </div>
        </div>
      </div>

      <div className="border-primary bg-primary flex w-full min-w-0 shrink-0 flex-wrap items-center gap-3 border-t px-3 py-2.5 sm:px-4">
        <div className="min-w-0 flex-1 basis-full xl:basis-0">
          <SyncedDualPlaybackControls
            actorIdAxel={PREVIEW_ACTOR_AXEL}
            actorIdUser={PREVIEW_ACTOR_USER}
            axelReady={axelReady}
          />
        </div>
        <div className="shrink-0 xl:ml-auto">
          <Checkbox
            id="preview-show-skeleton"
            checked={showSkeletonHelper}
            setChecked={setShowSkeletonHelper}
            variant="checkbox"
            label="Show skeleton"
          />
        </div>
      </div>
    </div>
  );
}

export default function CartographerMotionPreviewModal({
  open,
  onOpenChange,
  apiKey,
  characterID,
  meshPath,
  meshFile = null,
  meshFormat,
  externalMotionId = null,
  externalMotionLabel = null,
  bodyIndex,
  userCharacterDisplayName = null,
  characterMappingState = 'saved',
  mappingRevisionKey = null,
  configSource = 'saved',
  prepareForUserMotionRequest,
  motionExportSettings,
  onMotionExportSettingsChange,
  onRetargetStatusChange,
  onSwitchToUnsavedRetarget,
  onBackToMotionSearch,
}: CartographerMotionPreviewModalProps) {
  const isCompactViewport = useMediaQuery('(max-height: 860px)');
  const [motionSettingsOpen, setMotionSettingsOpen] =
    useState(!isCompactViewport);
  const [meshDownloadBusy, setMeshDownloadBusy] = useState(false);
  const [meshDownloadError, setMeshDownloadError] = useState<string | null>(
    null,
  );
  const resolvedMotionExportSettings =
    motionExportSettings ?? DEFAULT_MOTION_EXPORT_SETTINGS;
  const {
    fps: exportFps,
    frameStep: exportFrameStep,
    rootMotion: exportRootMotion,
  } = resolvedMotionExportSettings;

  const updateMotionExportSettings = useCallback(
    (settings: Partial<MotionExportSettings>) => {
      onMotionExportSettingsChange?.({
        ...resolvedMotionExportSettings,
        ...settings,
      });
    },
    [onMotionExportSettingsChange, resolvedMotionExportSettings],
  );

  const effectiveMotionId = externalMotionId;

  const motionNameSlug = useMemo(
    () =>
      resolveMotionNameSlug({
        externalMotionLabel,
        effectiveMotionId,
      }),
    [externalMotionLabel, effectiveMotionId],
  );

  const userCharacterSlug = useMemo(
    () =>
      sanitizeFileNameSegment(
        userCharacterDisplayName?.trim() || characterID.trim() || 'character',
        'character',
      ),
    [userCharacterDisplayName, characterID],
  );

  const userCharacterPanelTitle = useMemo(
    () =>
      userCharacterDisplayName?.trim() ||
      characterID.trim() ||
      'Your character',
    [userCharacterDisplayName, characterID],
  );

  const referenceBvhDownloadFilename = `cartwheel-axel-${motionNameSlug}.bvh`;
  const userBvhDownloadFilename = `${userCharacterSlug}-${motionNameSlug}.bvh`;

  const meshDownloadProfile = useMemo(
    () =>
      meshPath ? getCharacterMeshDownloadProfile(meshFormat, meshPath) : null,
    [meshFormat, meshPath],
  );
  const userAssetKind = useMemo(
    () =>
      resolveMotionPreviewAssetKind({
        meshFormat,
        meshPath,
      }),
    [meshFormat, meshPath],
  );
  const userPreviewDownloadType = useMemo(
    () => resolveMotionPreviewDownloadType(userAssetKind),
    [userAssetKind],
  );

  const {
    motionUrl: userMotionUrl,
    mainError,
    loading,
    previewOutOfDate,
    refreshNow,
  } = useCartographerMotionRetargetPreview({
    enabled: open && Boolean(meshPath),
    apiKey,
    motionID: effectiveMotionId,
    characterID,
    bodyIndex,
    configSource,
    downloadType: userPreviewDownloadType,
    fps: exportFps,
    frameStep: exportFrameStep,
    rootMotion: exportRootMotion,
    stateRevisionKey: mappingRevisionKey,
    resetOnDisable: false,
    prepareForUserMotionRequest,
  });
  const { motionUrl: axelBvhUrl, loading: axelLoading } =
    useCartographerMotionRetargetPreview({
      enabled: open && Boolean(meshPath),
      apiKey,
      motionID: effectiveMotionId,
      characterID: AXEL_CHARACTER_ID,
      bodyIndex,
      downloadType: 'bvh',
      fps: exportFps,
      frameStep: exportFrameStep,
      rootMotion: exportRootMotion,
      resetOnDisable: false,
    });

  useEffect(() => {
    if (!open) {
      setMeshDownloadError(null);
    }
  }, [open]);

  useEffect(() => {
    onRetargetStatusChange?.({
      source: 'modal',
      motionID: effectiveMotionId,
      bodyIndex,
      configSource,
      mappingRevisionKey,
      exportSettingsKey: getMotionExportSettingsKey(
        resolvedMotionExportSettings,
      ),
      userMotionUrl,
      loading,
      mainError,
      previewOutOfDate,
    });
  }, [
    bodyIndex,
    configSource,
    effectiveMotionId,
    loading,
    mainError,
    mappingRevisionKey,
    onRetargetStatusChange,
    previewOutOfDate,
    resolvedMotionExportSettings,
    userMotionUrl,
  ]);

  useEffect(() => {
    if (!open) return;
    setMotionSettingsOpen(!isCompactViewport);
  }, [isCompactViewport, open]);

  const handleDownloadAsCharacterFormat = useCallback(async () => {
    setMeshDownloadBusy(true);
    setMeshDownloadError(null);
    try {
      const result = await downloadCharacterMotionExport({
        apiKey,
        characterID,
        motionID: effectiveMotionId,
        bodyIndex,
        configSource,
        meshDownloadProfile,
        settings: resolvedMotionExportSettings,
        userMotionUrl,
        filename: `${userCharacterSlug}-${motionNameSlug}.${meshDownloadProfile?.fileExtension ?? 'fbx'}`,
        prepareForUserMotionRequest,
      });
      if (!result.ok && result.error) {
        setMeshDownloadError(result.error);
      }
    } finally {
      setMeshDownloadBusy(false);
    }
  }, [
    apiKey,
    bodyIndex,
    characterID,
    effectiveMotionId,
    configSource,
    meshDownloadProfile,
    motionNameSlug,
    prepareForUserMotionRequest,
    resolvedMotionExportSettings,
    userMotionUrl,
    userCharacterSlug,
  ]);

  const handleRefreshPreview = useCallback(async () => {
    if (configSource === 'temp') {
      refreshNow();
      return;
    }
    if (!onSwitchToUnsavedRetarget) return;
    const didSwitch = await onSwitchToUnsavedRetarget();
    if (!didSwitch) return;
    refreshNow({ configSource: 'temp' });
  }, [configSource, onSwitchToUnsavedRetarget, refreshNow]);

  const handleCopyMotionId = useCallback(async () => {
    if (!externalMotionId) return;
    try {
      await navigator.clipboard.writeText(externalMotionId);
      noticesStore.success(
        `${externalMotionId} has been copied to the clipboard`,
      );
    } catch {
      noticesStore.error('Could not copy to clipboard');
    }
  }, [externalMotionId]);

  const showBackToMotionSearch = Boolean(
    externalMotionId && onBackToMotionSearch,
  );
  const closeButtonText = externalMotionId ? 'Minimize' : 'Close';
  const mappingStatus =
    characterMappingState === 'unsaved'
      ? {
          label: 'Using unsaved in-editor mapping',
          tooltip:
            'This retargeting was created using unsaved in-editor mapping',
          className: 'bg-yellow-50/15',
        }
      : {
          label: 'Using latest saved character mapping',
          tooltip:
            'This retargeting was created using your latest saved character mapping',
          className: 'bg-secondary',
        };
  const playerRootKey = `${characterID}-${meshPath}-${effectiveMotionId}-dual-preview-${userAssetKind}-${userMotionUrl ?? ''}-${axelBvhUrl ?? ''}`;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={null}
      maxWidth={1120}
      horizontalBleed
      verticalBleed
      fixedHeightContent
      fullscreenOnMobile
      contentClassName="!bg-transparent shadow-none dark:!bg-transparent"
    >
      {open && meshPath ? (
        <div className="flex h-full min-h-0 w-full flex-1 flex-col px-2 pb-2 pt-2 sm:px-3">
          <div className="bg-player-overlay shadow-player-overlay flex min-h-0 flex-1 flex-col rounded-2xl p-2">
            <div className="bg-primary border-primary flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border">
              <div
                className={`border-primary border-b ${isCompactViewport ? 'px-4 py-4' : 'px-6 py-5'}`}
              >
                <div className="flex items-start gap-3">
                  {showBackToMotionSearch ? (
                    <button
                      type="button"
                      className="text-primary border-primary hover:bg-gray-15 dark:hover:bg-gray-75 mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border transition-colors"
                      aria-label="Back to motion search"
                      onClick={onBackToMotionSearch}
                    >
                      <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <Text
                          as="h2"
                          variant="studio/title/md"
                          className="text-primary"
                        >
                          Motion preview
                        </Text>
                        <Text
                          variant="studio/body/medium/sm"
                          className="text-secondary mt-1.5"
                        >
                          Motion retargeted onto {userCharacterPanelTitle}
                        </Text>
                      </div>
                      <div className="flex shrink-0 justify-end">
                        <Tooltip
                          text={mappingStatus.tooltip}
                          side="bottom"
                          inModal
                        >
                          <div
                            className={`${mappingStatus.className} inline-flex rounded-lg px-3 py-2`}
                          >
                            <Text
                              variant="studio/body/medium/sm"
                              className="text-primary whitespace-nowrap"
                            >
                              {mappingStatus.label}
                            </Text>
                          </div>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {externalMotionLabel ? (
                <div
                  className={`border-primary flex flex-wrap items-center justify-between gap-3 border-b ${isCompactViewport ? 'px-4 py-3' : 'px-6 py-4'}`}
                >
                  <Text
                    variant="studio/title/md"
                    className="text-primary min-w-0 flex-1 truncate"
                    title={externalMotionLabel}
                  >
                    {externalMotionLabel}
                  </Text>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    icon={<LinkIcon />}
                    disabled={!externalMotionId}
                    onClick={() => void handleCopyMotionId()}
                  >
                    Copy motion ID
                  </Button>
                </div>
              ) : null}

              <MotionPreviewSection
                title="Motion settings"
                open={motionSettingsOpen}
                onToggle={() => setMotionSettingsOpen((prev) => !prev)}
              >
                <div
                  className={`grid grid-cols-1 gap-4 sm:grid-cols-3 ${
                    isCompactViewport ? 'sm:gap-4' : 'sm:gap-6'
                  }`}
                >
                  <div className="flex flex-col gap-2.5">
                    <Text variant="studio/body/medium/sm">Frame rate</Text>
                    <Select
                      inModal
                      value={exportFps}
                      onValueChange={(value) =>
                        updateMotionExportSettings({
                          fps: Number.parseFloat(value),
                        })
                      }
                      items={MOTION_DOWNLOAD_FPS_OPTIONS.map((rate) => ({
                        label: rate.toString(),
                        value: rate,
                      }))}
                    />
                  </div>
                  <Input
                    name="cartographer-export-frame-step"
                    label="Frame step (1-12)"
                    type="number"
                    min={1}
                    max={12}
                    value={String(exportFrameStep)}
                    onChange={(e) => {
                      const v = Number.parseInt(e.target.value, 10);
                      updateMotionExportSettings({
                        frameStep:
                          Number.isInteger(v) && v >= 1 && v <= 12 ? v : 1,
                      });
                    }}
                    className="w-full"
                  />
                  <div className="flex flex-col gap-2.5">
                    <Text variant="studio/body/medium/sm">Root motion</Text>
                    <div className="flex items-center pt-2">
                      <Checkbox
                        id="cartographer-export-root-motion"
                        checked={!exportRootMotion}
                        label="Move in place"
                        variant="checkbox"
                        color="settings"
                        setChecked={(checked) =>
                          updateMotionExportSettings({ rootMotion: !checked })
                        }
                      />
                    </div>
                  </div>
                </div>
              </MotionPreviewSection>

              {mainError && !userMotionUrl ? (
                <div className="styled-scrollbar flex min-h-0 flex-1 flex-col items-center justify-center gap-2 overflow-y-auto px-4 py-8">
                  <Text
                    variant="studio/body/md"
                    className="text-center text-red-600"
                  >
                    {mainError}
                  </Text>
                </div>
              ) : !userMotionUrl ? (
                <div className="styled-scrollbar flex min-h-[320px] flex-1 items-center justify-center overflow-y-auto px-4 py-12">
                  <div className="text-secondary flex items-center gap-3">
                    <Loading className="h-5 w-5" strokeWidth={3} />
                    <Text variant="studio/body/md" className="text-secondary">
                      Retargeting motion…
                    </Text>
                  </div>
                </div>
              ) : (
                <Player.Root
                  key={playerRootKey}
                  id="cartographer-motion-preview-dual"
                  initialSettings={{
                    playback: { autoplay: false, loop: true },
                    character: { showSkeleton: false },
                  }}
                >
                  <div className="styled-scrollbar flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-2 pt-3 sm:px-4">
                    <DualMotionPreviewPlayers
                      axelBvhUrl={axelBvhUrl}
                      axelLoading={axelLoading}
                      userMotionUrl={userMotionUrl}
                      userAssetKind={userAssetKind}
                      characterID={characterID}
                      userCharacterPanelTitle={userCharacterPanelTitle}
                      meshPath={meshPath}
                      meshFile={meshFile}
                      meshFormat={meshFormat}
                      exportFps={exportFps}
                      exportFrameStep={exportFrameStep}
                      exportRootMotion={exportRootMotion}
                      referenceBvhDownloadFilename={
                        referenceBvhDownloadFilename
                      }
                      userBvhDownloadFilename={userBvhDownloadFilename}
                      compactHeight={isCompactViewport}
                      denseHeight={motionSettingsOpen && isCompactViewport}
                    />
                  </div>
                </Player.Root>
              )}
              <>
                <div className="flex flex-wrap items-center justify-end gap-2 px-4 pb-3 pt-2 sm:px-6">
                  {previewOutOfDate && userMotionUrl ? (
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={loading}
                      icon={<RetargetRefreshIcon className="h-4 w-4" />}
                      onClick={() => void handleRefreshPreview()}
                    >
                      Retarget with current mapping
                    </Button>
                  ) : null}
                  <Button
                    type="button"
                    variant="secondary"
                    icon={<CrossIcon className="h-4 w-4" />}
                    onClick={() => onOpenChange(false)}
                  >
                    {closeButtonText}
                  </Button>
                  {meshDownloadProfile ? (
                    <Button
                      variant="primary"
                      disabled={
                        loading ||
                        !userMotionUrl ||
                        Boolean(mainError) ||
                        meshDownloadBusy
                      }
                      onClick={handleDownloadAsCharacterFormat}
                    >
                      Download as {meshDownloadProfile.label}
                    </Button>
                  ) : null}
                </div>
                {meshDownloadProfile && meshDownloadError ? (
                  <Text
                    variant="studio/body/medium/xs"
                    className="px-4 pb-2 text-center text-red-600 sm:px-6"
                  >
                    {meshDownloadError}
                  </Text>
                ) : null}
              </>
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
